
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalRoundStats } from "@/hooks/useCourseStats";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface PrecisionEvolutionChartProps {
  stats: ExternalRoundStats[];
}

const PrecisionEvolutionChart = ({ stats }: PrecisionEvolutionChartProps) => {
  const { t, i18n } = useTranslation('course-stats');

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Sort by date ascending for chart
  const sortedStats = [...stats].sort(
    (a, b) => new Date(a.round_date).getTime() - new Date(b.round_date).getTime()
  );

  const chartData = sortedStats.map((stat) => ({
    date: format(new Date(stat.round_date), "dd/MM", { locale: getLocale() }),
    fullDate: format(new Date(stat.round_date), "PPP", { locale: getLocale() }),
    fairwayPercentage: stat.fairway_percentage,
    girPercentage: stat.gir_percentage,
    course: stat.course_name,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{payload[0]?.payload?.fullDate}</p>
          <p className="text-sm text-muted-foreground">{payload[0]?.payload?.course}</p>
          <div className="mt-2 space-y-1">
            {payload.map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value?.toFixed(0)}%
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('charts.precisionEvolution')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
              label={{ value: '%', angle: -90, position: 'insideLeft', fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="fairwayPercentage"
              name={t('charts.fairwaysPercent')}
              stroke="#059669"
              fill="#059669"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="girPercentage"
              name={t('charts.girPercent')}
              stroke="#ea580c"
              fill="#ea580c"
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default PrecisionEvolutionChart;
